import { useState, type FormEvent, type ReactElement } from 'react';
import { ApiError } from '../shared/api/client';
import { api, type DriverView } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { useDepartments } from '../shared/lib/departments';
import {
  DRIVER_LANGUAGES,
  EMPLOYMENT_KINDS,
  EXTERNAL_CODE_MAX,
  FULL_NAME_MAX,
  driverFormFrom,
  emptyDriverForm,
  errorsFromProblem,
  hasErrors,
  toDriverInput,
  toDriverPatch,
  validateDriverForm,
  type DriverField,
  type DriverFormErrors,
  type DriverFormState,
} from '../shared/lib/driver-form';
import { formatPhoneInput } from '../shared/lib/phone';
import {
  DISABLED_OPACITY,
  FieldNote,
  FormAlert,
  FormField,
  actionsStyle,
  checkStyle,
  checksStyle,
  errorStyle,
  fieldsetStyle,
  formStyle,
  labelStyle,
  pairStyle,
  supportCode,
} from '../shared/ui/form';
import { Drawer, primaryButtonStyle, secondaryButtonStyle } from '../shared/ui/primitives';

/**
 * Заведение и правка курьера — правая панель поверх списка (разд. 12: «список
 * слева-таблицей, редактирование в drawer справа»).
 *
 * Без `driver` — заведение (`POST /web/drivers`), с ним — правка
 * (`PATCH /web/drivers/{id}`), куда уходит только изменённое. Язык в правке не
 * показывается: операция его не принимает, курьер меняет язык сам в приложении.
 *
 * Отделы — только свои: сервер всё равно отклонит курьера в чужом отделе
 * (`department_forbidden`), и узнавать об этом отказом хуже, чем не видеть варианта.
 *
 * Сохранение пессимистичное (разд. 15): кнопка блокируется до ответа, ошибка — рядом
 * с полем, а не тостом. Повторное нажатие после сбоя сети не заведёт второго курьера:
 * номер телефона уникален, и сервер ответит `phone_taken`.
 */
export function DriverDrawer({
  driver,
  onClose,
  onCreated,
  onSaved,
}: {
  readonly driver?: DriverView | undefined;
  readonly onClose: () => void;
  readonly onCreated: (driver: DriverView) => void;
  readonly onSaved?: ((driver: DriverView) => void) | undefined;
}): ReactElement {
  const messages = useMessages();
  const text = messages.cabinet.drivers;
  const departments = useDepartments();
  const editing = driver !== undefined;

  const [original] = useState<DriverFormState>(() =>
    driver === undefined ? emptyDriverForm(departments) : driverFormFrom(driver),
  );
  const [form, setForm] = useState<DriverFormState>(original);
  const [errors, setErrors] = useState<DriverFormErrors>({});
  // Поля проверяются после первой попытки сохранить: подсвечивать красным пустую форму,
  // которую человек ещё не начал заполнять, — шум.
  const [submitted, setSubmitted] = useState(false);
  const [general, setGeneral] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Отдел курьера, которого у администратора нет, всё равно показывается: снять с
  // курьера чужой отдел нельзя, но и прятать его — значит врать о составе.
  const shownModes = [...new Set([...departments, ...original.modes])];

  const update = (patch: Partial<DriverFormState>): void => {
    const next = { ...form, ...patch };

    setForm(next);

    if (submitted) {
      setErrors(validateDriverForm(next));
    }
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitted(true);
    setGeneral(null);

    const found = validateDriverForm(form);
    setErrors(found);

    if (hasErrors(found)) {
      return;
    }

    setPending(true);

    try {
      if (driver === undefined) {
        onCreated(await api.createDriver(toDriverInput(form)));
      } else {
        const body = toDriverPatch(form, original);

        // Ничего не изменилось — запрос не нужен, а в аудите не появится пустая правка.
        if (Object.keys(body).length === 0) {
          onClose();

          return;
        }

        const saved = await api.updateDriver(driver.id, body);
        (onSaved ?? onCreated)(saved);
      }
    } catch (cause) {
      if (!(cause instanceof ApiError)) {
        setGeneral(editing ? text.errors.saveFailed : text.errors.failed);

        return;
      }

      const mapped = errorsFromProblem(cause);
      setErrors(mapped.fields);

      if (mapped.general) {
        setGeneral(`${generalText(cause)} · ${text.errors.code}: ${supportCode(cause)}`);
      }
    } finally {
      setPending(false);
    }
  };

  const generalText = (cause: ApiError): string => {
    if (cause.code === 'validation_failed') {
      return text.errors.validation;
    }

    if (cause.status === FORBIDDEN) {
      return editing ? text.errors.forbiddenEdit : text.errors.forbidden;
    }

    return editing ? text.errors.saveFailed : text.errors.failed;
  };

  const errorOf = (field: DriverField): string | null => {
    const code = errors[field];

    return code === undefined ? null : text.errors[code];
  };

  return (
    <Drawer
      title={<h2>{editing ? text.editTitle : text.createTitle}</h2>}
      label={editing ? text.editTitle : text.createTitle}
      onClose={onClose}
      trapFocus
    >
      <form noValidate onSubmit={(event) => void submit(event)} style={formStyle}>
        <FormField
          id="driver-full-name"
          label={text.fullName}
          required={text.required}
          error={errorOf('full_name')}
        >
          <input
            id="driver-full-name"
            value={form.full_name}
            maxLength={FULL_NAME_MAX}
            autoComplete="off"
            aria-invalid={errors.full_name !== undefined}
            aria-describedby={errors.full_name === undefined ? undefined : 'driver-full-name-error'}
            onChange={(event) => update({ full_name: event.target.value })}
          />
        </FormField>

        <FormField
          id="driver-phone"
          label={text.phone}
          required={text.required}
          hint={text.phoneHint}
          error={errorOf('phone')}
        >
          <input
            id="driver-phone"
            className="num"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            value={form.phone}
            aria-invalid={errors.phone !== undefined}
            aria-describedby={
              errors.phone === undefined ? 'driver-phone-hint' : 'driver-phone-error'
            }
            onChange={(event) => update({ phone: formatPhoneInput(event.target.value) })}
          />
        </FormField>

        <div style={pairStyle}>
          <FormField id="driver-employment" label={text.employment} error={null}>
            <select
              id="driver-employment"
              value={form.employment}
              onChange={(event) =>
                update({ employment: event.target.value as DriverFormState['employment'] })
              }
            >
              <option value="">{text.employmentNone}</option>
              {EMPLOYMENT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {text.employmentKinds[kind]}
                </option>
              ))}
            </select>
          </FormField>

          {editing ? null : (
            <FormField id="driver-language" label={text.language} error={null}>
              <select
                id="driver-language"
                value={form.language}
                onChange={(event) =>
                  update({ language: event.target.value as DriverFormState['language'] })
                }
              >
                {DRIVER_LANGUAGES.map((language) => (
                  <option key={language} value={language}>
                    {text.languages[language]}
                  </option>
                ))}
              </select>
            </FormField>
          )}
        </div>

        <FormField
          id="driver-external-code"
          label={text.externalCode}
          hint={text.externalCodeHint}
          error={errorOf('external_code')}
        >
          <input
            id="driver-external-code"
            className="num"
            value={form.external_code}
            maxLength={EXTERNAL_CODE_MAX}
            autoComplete="off"
            aria-invalid={errors.external_code !== undefined}
            aria-describedby={
              errors.external_code === undefined
                ? 'driver-external-code-hint'
                : 'driver-external-code-error'
            }
            onChange={(event) => update({ external_code: event.target.value })}
          />
        </FormField>

        <fieldset
          style={fieldsetStyle}
          aria-describedby={errors.modes === undefined ? 'driver-modes-hint' : 'driver-modes-error'}
        >
          <legend style={labelStyle}>{`${text.departments} · ${text.required}`}</legend>
          <div style={checksStyle}>
            {shownModes.length === 0 ? (
              <span style={errorStyle}>{messages.cabinet.users.noDepartment}</span>
            ) : null}
            {shownModes.map((mode) => (
              <label key={mode} style={checkStyle}>
                <input
                  type="checkbox"
                  checked={form.modes.includes(mode)}
                  // Чужой отдел снять нельзя: сервер ответит `department_forbidden`.
                  disabled={!departments.includes(mode)}
                  onChange={() =>
                    update({
                      modes: form.modes.includes(mode)
                        ? form.modes.filter((item) => item !== mode)
                        : [...form.modes, mode],
                    })
                  }
                />
                <span>{messages.cabinet.modes[mode]}</span>
              </label>
            ))}
          </div>
          <FieldNote id="driver-modes" hint={text.departmentsHint} error={errorOf('modes')} />
        </fieldset>

        <div>
          <label style={checkStyle}>
            <input
              type="checkbox"
              checked={form.is_active}
              aria-describedby={errors.is_active === undefined ? undefined : 'driver-active-error'}
              onChange={(event) => update({ is_active: event.target.checked })}
            />
            <span>{text.active}</span>
          </label>
          <FieldNote id="driver-active" error={errorOf('is_active')} />
        </div>

        <FormAlert text={general} />

        <div style={actionsStyle}>
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>
            {messages.cabinet.common.cancel}
          </button>
          <button
            type="submit"
            disabled={pending}
            aria-busy={pending}
            style={{ ...primaryButtonStyle, opacity: pending ? DISABLED_OPACITY : 1 }}
          >
            {pending ? text.saving : editing ? text.save : text.submit}
          </button>
        </div>
      </form>
    </Drawer>
  );
}

const FORBIDDEN = 403;
